import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown } from 'lucide-react';
import {
  ManualCapacityDisplaySchema,
  type StepConflict,
  type StepFieldChange
} from '@bliks-io/contracts';
import {
  Button,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader
} from '@bliks-io/ui';
import { getFieldLabel } from '../publish/publishErrorFormatting';
import { manualCapacityLabel } from './manualCapacityLabel';

interface Props {
  conflicts: StepConflict[];
  /**
   * Triggered by Cancel button and outside-click. Default action — visually
   * primary because it's the safe choice ("keep my work, decide later").
   */
  onCancel: () => void;
  /**
   * Discard the draft entirely and recreate from current live state. Two-step
   * confirm in the UI: first click arms the button (turns destructive red),
   * second click invokes this callback. Caller is responsible for performing
   * the destroy + recreate.
   */
  onDiscardAndRecreate: () => void;
  /**
   * Publish despite the live changes (force=true on the publish request).
   * Optional — pass null to hide the button (e.g. in contexts where force-
   * publish isn't allowed).
   */
  onPublishAnyway: (() => void) | null;
  isProcessing: boolean;
}

export function DraftConflictDialog({
  conflicts,
  onCancel,
  onDiscardAndRecreate,
  onPublishAnyway,
  isProcessing
}: Props) {
  const { t } = useTranslation();
  // When set, the dialog swaps its content to a focused destructive-confirm
  // view — explicit consent before an irreversible action. Same dialog
  // instance, no second portal, no z-index risk. Two danger paths:
  //  - 'discard': drop the entire draft and recreate from live.
  //  - 'publish': force-publish, overwriting the live process (and any
  //    in-flight viral recordings on it).
  const [confirming, setConfirming] = useState<'discard' | 'publish' | null>(null);

  let content: ReactNode;
  if (confirming === 'discard') {
    content = (
      <DangerConfirmView
        titleKey="processDraft.draftConflict.discardModalTitle"
        bodyKey="processDraft.draftConflict.discardModalBody"
        cancelKey="processDraft.draftConflict.discardModalCancel"
        confirmKey="processDraft.draftConflict.discardModalConfirm"
        onClose={onCancel}
        onBack={() => setConfirming(null)}
        onConfirm={onDiscardAndRecreate}
        isProcessing={isProcessing}
      />
    );
  } else if (confirming === 'publish' && onPublishAnyway) {
    content = (
      <DangerConfirmView
        titleKey="processDraft.draftConflict.publishAnywayModalTitle"
        bodyKey="processDraft.draftConflict.publishAnywayModalBody"
        cancelKey="processDraft.draftConflict.publishAnywayModalCancel"
        confirmKey="processDraft.draftConflict.publishAnywayModalConfirm"
        onClose={onCancel}
        onBack={() => setConfirming(null)}
        onConfirm={onPublishAnyway}
        isProcessing={isProcessing}
      />
    );
  } else {
    content = (
      <>
        <DialogShellHeader
          icon={<AlertCircle className="w-5 h-5 text-warning" />}
          iconTone="amber"
          title={t('processDraft.draftConflict.title')}
          subtitle={t('processDraft.draftConflict.body')}
          onClose={onCancel}
        />
        <DialogShellBody>
          {conflicts.map((conflict) => (
            <StepConflictBlock key={conflict.stepId} conflict={conflict} />
          ))}
        </DialogShellBody>
        <DialogShellFooter
          leftSlot={
            <Button
              variant="ghost"
              wrap
              onClick={() => setConfirming('discard')}
              disabled={isProcessing}
            >
              {t('processDraft.draftConflict.discard')}
            </Button>
          }
          primary={
            onPublishAnyway
              ? {
                  label: t('processDraft.draftConflict.publishAnyway'),
                  onClick: () => setConfirming('publish'),
                  disabled: isProcessing
                }
              : {
                  label: t('processDraft.draftConflict.cancel'),
                  onClick: onCancel,
                  disabled: isProcessing
                }
          }
        />
      </>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogShell size="md">{content}</DialogShell>
    </Dialog>
  );
}

/**
 * Focused two-step destructive-confirm view rendered inside the conflict
 * dialog. Shared by the "discard draft" and "publish anyway" danger paths —
 * same red-toned header + secondary("go back") / danger("confirm") footer.
 */
function DangerConfirmView({
  titleKey,
  bodyKey,
  cancelKey,
  confirmKey,
  onClose,
  onBack,
  onConfirm,
  isProcessing
}: {
  titleKey: string;
  bodyKey: string;
  cancelKey: string;
  confirmKey: string;
  onClose: () => void;
  onBack: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <DialogShellHeader
        icon={<AlertCircle className="w-5 h-5 text-danger" />}
        iconTone="danger"
        title={t(titleKey)}
        subtitle={t(bodyKey)}
        onClose={onClose}
      />
      <DialogShellFooter
        secondary={{
          label: t(cancelKey),
          onClick: onBack,
          disabled: isProcessing
        }}
        danger={{
          label: t(confirmKey),
          onClick: onConfirm,
          loading: isProcessing
        }}
      />
    </>
  );
}

function StepConflictBlock({ conflict }: { conflict: StepConflict }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const bodyId = useId();
  const label = conflict.stepLabel || t('processDraft.draftConflict.unnamedStep');
  const changeCount = conflict.changes.length;

  return (
    <div className="border rounded-md bg-surface-subtle">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left rounded-md focus:outline-none focus:ring-2 focus:ring-info-solid"
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${
              expanded ? '' : '-rotate-90'
            }`}
          />
          <span className="text-sm font-medium text-foreground truncate">{label}</span>
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {changeCount === 0
            ? t('processDraft.draftConflict.empty')
            : t('processDraft.draftConflict.changesCount', {
                count: changeCount
              })}
        </span>
      </button>
      {expanded && (
        <div id={bodyId} className="px-3 pb-3">
          {changeCount === 0 ? (
            <div className="text-xs text-fg-secondary">{t('processDraft.draftConflict.empty')}</div>
          ) : (
            <ul className="space-y-3">
              {conflict.changes.map((change, idx) => (
                <li key={`${change.field}-${idx}`}>
                  <FieldChangeRow change={change} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function FieldChangeRow({ change }: { change: StepFieldChange }) {
  const { t } = useTranslation();
  const label = getFieldLabel(t, change.field);
  // A manual capacity change carries the entered primitives, not a display string.
  const diffValue = (value: unknown): unknown => {
    if (change.field !== 'manualCapacity') return value;
    const primitives = ManualCapacityDisplaySchema.safeParse(value);
    return primitives.success ? manualCapacityLabel(primitives.data, t) : null;
  };

  if (change.kind === 'list') {
    const liveItems = [
      ...change.removed.map((item) => ({
        id: `r-${item.id}`,
        label: itemLabel(item.label, t)
      })),
      ...(change.modified ?? []).map((item) => ({
        id: `m-${item.id}`,
        label: itemLabel(item.liveLabel, t)
      }))
    ];
    const draftItems = [
      ...change.added.map((item) => ({
        id: `a-${item.id}`,
        label: itemLabel(item.label, t)
      })),
      ...(change.modified ?? []).map((item) => ({
        id: `m-${item.id}`,
        label: itemLabel(item.draftLabel, t)
      }))
    ];
    return (
      <div className="text-xs text-fg-secondary">
        <div className="font-medium mb-1">{label}</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <ListDiffPanel
            caption={t('processDraft.draftConflict.live')}
            items={liveItems}
            tone="live"
          />
          <ListDiffPanel
            caption={t('processDraft.draftConflict.yours')}
            items={draftItems}
            tone="yours"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs text-fg-secondary">
      <div className="font-medium mb-1">{label}</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <DiffPanel
          caption={t('processDraft.draftConflict.live')}
          value={diffValue(change.live)}
          tone="live"
        />
        <DiffPanel
          caption={t('processDraft.draftConflict.yours')}
          value={diffValue(change.draft)}
          tone="yours"
        />
      </div>
    </div>
  );
}

function itemLabel(label: string, t: (key: string) => string): string {
  return label.length > 0 ? label : t('processDraft.draftConflict.unnamedItem');
}

function DiffPanelShell({
  caption,
  tone,
  children
}: {
  caption: string;
  tone: 'live' | 'yours';
  children: ReactNode;
}) {
  const toneClasses =
    tone === 'live'
      ? 'bg-danger-surface border-danger-line text-danger-strong'
      : 'bg-positive-surface border-positive-line text-positive-strong';
  const captionClasses = tone === 'live' ? 'text-danger' : 'text-positive';
  return (
    <div className={`flex-1 min-w-0 rounded border px-2 py-1.5 ${toneClasses}`}>
      <div className={`text-3xs uppercase tracking-wide ${captionClasses}`}>{caption}</div>
      {children}
    </div>
  );
}

function ListDiffPanel({
  caption,
  items,
  tone
}: {
  caption: string;
  items: { id: string; label: string }[];
  tone: 'live' | 'yours';
}) {
  const { t } = useTranslation();
  return (
    <DiffPanelShell caption={caption} tone={tone}>
      {items.length === 0 ? (
        <div className="text-fg-subtle italic">{t('processDraft.draftConflict.listEmpty')}</div>
      ) : (
        <ul className="list-disc pl-4 break-words">
          {items.map((item) => (
            <li key={item.id}>{item.label}</li>
          ))}
        </ul>
      )}
    </DiffPanelShell>
  );
}

function DiffPanel({
  caption,
  value,
  tone
}: {
  caption: string;
  value: unknown;
  tone: 'live' | 'yours';
}) {
  return (
    <DiffPanelShell caption={caption} tone={tone}>
      <div className="break-words whitespace-pre-wrap">{formatValue(value)}</div>
    </DiffPanelShell>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  // Populated assignee shape from the backend: `{ id, name, model }`
  if (typeof value === 'object' && 'name' in (value as object)) {
    const name = (value as { name?: string }).name;
    return name && name.length > 0 ? name : '—';
  }
  return JSON.stringify(value);
}
